// ============================================================================
// FSA NOTE MODAL — ADDITIONAL DISCLOSURES PANE
// ============================================================================
// Renders entries from the template's additional_disclosures JSONB spec.
// Each entry collapses/expands independently. Stage 1: free-text disclosures
// are fully editable; structured grids (column_spec / fixed_rows / computed)
// render a read-only preview with an "editor coming next iteration" note.
//
// Engagement-side values live at workspace.additional_disclosures[code]:
//   - free_text          → string
//   - column_spec        → array of row objects keyed by column codes
//   - fixed_rows         → object/array keyed by particulars/code
//   - is_computed        → number / string (read-only)

import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  NoteWorkspaceData,
  DisclosureValueSourceLive,
} from 'api/types/fsa/noteData.types';
import DisclosureVariableGrid, {
  type ColumnSpec,
} from './disclosures/DisclosureVariableGrid';
import DisclosureFixedGrid, {
  recomputeFixedGridFromSpec,
  type FixedRow,
  type Row as DisclosureRow,
} from './disclosures/DisclosureFixedGrid';
import type { RoundingFactor } from '../shared/rounding';
import type { ValidationRule } from './disclosures/validations';
import type { DisplayMode } from './MainNotePane';

interface DisclosureSpec {
  code: string;
  label: string;
  description?: string;
  free_text?: boolean;
  is_computed_disclosure?: boolean;
  column_spec?: ColumnSpec[];
  fixed_rows?: Record<string, unknown>[];
  formula?: string;
  validations?: ValidationRule[];
  [key: string]: unknown;
}

interface Props {
  additionalDisclosuresSpecJson?: string | null;
  workspace: NoteWorkspaceData;
  setDisclosure: (code: string, value: unknown) => void;
  /** Toggle a section's presence in workspace._hidden_sections. PDF
   *  renderer skips any spec whose code is in the list. UI still shows
   *  the section so the auditor can review what's being hidden. */
  toggleDisclosureHidden: (code: string) => void;
  /** One-click helper — flag every disclosure section with no saved
   *  value as hidden. Called from the pane header button. The pane
   *  passes the list of known spec codes so the helper only acts on
   *  codes the engagement's template actually exposes. */
  autoHideEmptyDisclosures: (specCodes: string[]) => void;
  readOnly: boolean;
  /** Engagement's rounding factor key — surfaces as a "(₹ in <unit>)"
   *  hint above any disclosure card that has at least one amount column.
   *  Lets the auditor see at a glance that amounts are entered in rounded
   *  units (same convention as the main note's amount_rounded). */
  roundingFactorKey?: string | null;
  /** Note-Modal-level Actual / Rounded toggle — drives whether grids
   *  render the actual-value columns or the rounded-value columns. The
   *  toggle is owned by the modal so the same selection applies to the
   *  main class table and every disclosure grid. */
  displayMode: DisplayMode;
  setDisplayMode: (mode: DisplayMode) => void;
  /**
   * Live values for every disclosure fixed_row that declares a
   * `value_source` pull. Keyed by `"<disclosure_code>.<row_key>"`.
   * Used by DisclosureFixedGrid to detect drift between saved and
   * live values, and by the pane-level "Update from Sources" action
   * to refresh the saved rows on click.
   */
  valueSourceLiveValues?: Record<string, DisclosureValueSourceLive>;
}

const FACTOR_LABEL: Record<string, string> = {
  ACTUALS: 'Rupees',
  THOUSANDS: 'Rupees Thousands',
  LAKHS: 'Rupees Lakhs',
  CRORES: 'Rupees Crores',
};

const DisclosuresPane: React.FC<Props> = ({
  additionalDisclosuresSpecJson,
  workspace,
  setDisclosure,
  toggleDisclosureHidden,
  autoHideEmptyDisclosures,
  readOnly,
  roundingFactorKey,
  displayMode,
  setDisplayMode,
  valueSourceLiveValues,
}) => {
  // Voiding unused-variable warning on setDisplayMode for now — the
  // current pass uses displayMode read-only (driven by the modal's
  // toggle in the main pane). Wiring an in-pane mirror of the toggle
  // is a future polish step; the setter is forwarded for that path.
  void setDisplayMode;
  const hiddenSet = useMemo(
    () => new Set(workspace._hidden_sections ?? []),
    [workspace._hidden_sections],
  );
  const specs: DisclosureSpec[] = useMemo(() => {
    if (!additionalDisclosuresSpecJson) return [];
    try {
      const parsed = JSON.parse(additionalDisclosuresSpecJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [additionalDisclosuresSpecJson]);

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    // Start with first disclosure open (unless it's empty); rest collapsed.
    const initial = new Set<string>();
    if (specs.length > 0) initial.add(specs[0].code);
    return initial;
  });

  const toggle = (code: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  // Must run before any early return — Hooks must be called unconditionally.
  const specCodes = useMemo(() => specs.map(s => s.code), [specs]);

  // Split the flat `<disclosure_code>.<row_key>` live-values dict into
  // per-disclosure inner maps so each DisclosureFixedGrid receives only
  // its own scope. Empty object when no live values surfaced (schedule
  // has no value_source rows, or hydrate hasn't populated them yet).
  const liveByDisclosure = useMemo(() => {
    const out: Record<string, Record<string, DisclosureValueSourceLive>> = {};
    if (!valueSourceLiveValues) return out;
    for (const [key, live] of Object.entries(valueSourceLiveValues)) {
      const dot = key.indexOf('.');
      if (dot <= 0) continue;
      const disclosureCode = key.slice(0, dot);
      const rowKey = key.slice(dot + 1);
      if (!out[disclosureCode]) out[disclosureCode] = {};
      out[disclosureCode][rowKey] = live;
    }
    return out;
  }, [valueSourceLiveValues]);

  // Note-level "Update from Sources" affordance: drift exists when any
  // value_source row's saved value differs from the live source value.
  // Mirrors the MainNote "Update from Ledger" enable rule.
  const anyValueSourceDrift = useMemo(() => {
    if (!valueSourceLiveValues || Object.keys(valueSourceLiveValues).length === 0) return false;
    for (const spec of specs) {
      const rowsLive = liveByDisclosure[spec.code];
      if (!rowsLive) continue;
      const fixedRows = (spec.fixed_rows ?? []) as Array<{ row_key?: string }>;
      const savedRows = workspace.additional_disclosures[spec.code];
      const savedArr = Array.isArray(savedRows) ? savedRows : [];
      for (let i = 0; i < fixedRows.length; i++) {
        const rk = fixedRows[i]?.row_key;
        if (!rk || !rowsLive[rk]) continue;
        const live = rowsLive[rk];
        const saved = savedArr[i] as Record<string, unknown> | undefined;
        const savedActualCy = typeof saved?.value_cy === 'number' ? (saved.value_cy as number) : 0;
        const savedActualPy = typeof saved?.value_py === 'number' ? (saved.value_py as number) : 0;
        if (Math.abs(savedActualCy - live.actualCy) > 0.005) return true;
        if (Math.abs(savedActualPy - live.actualPy) > 0.005) return true;
      }
    }
    return false;
  }, [specs, liveByDisclosure, valueSourceLiveValues, workspace.additional_disclosures]);

  // Click handler for "Update from Sources" — walks every disclosure
  // with value-source rows, overwrites their saved value_cy / value_py
  // (and the __rounded companions) with the live values from hydrate.
  // Other rows on the disclosure (auditor-typed shares, par value,
  // computed EPS) are preserved as-saved. The auditor's next Save
  // persists the refreshed snapshot.
  const handleUpdateFromSources = () => {
    for (const spec of specs) {
      const rowsLive = liveByDisclosure[spec.code];
      if (!rowsLive) continue;
      const fixedRowsSpec = (spec.fixed_rows ?? []) as FixedRow[];
      const colSpec = (spec.column_spec ?? []) as ColumnSpec[];
      const savedRows = workspace.additional_disclosures[spec.code];
      const savedArr = Array.isArray(savedRows)
        ? (savedRows as DisclosureRow[])
        : [];
      // Pass 1: overwrite only the value-source rows with their live
      // values. Other rows on the disclosure (auditor-typed shares,
      // par value, dilutive count) stay as-saved.
      const overwritten: DisclosureRow[] = [];
      let mutated = false;
      for (let i = 0; i < fixedRowsSpec.length; i++) {
        const existing = (savedArr[i] ?? {}) as DisclosureRow;
        const rk = fixedRowsSpec[i]?.row_key;
        if (rk && rowsLive[rk]) {
          const live = rowsLive[rk];
          overwritten.push({
            ...existing,
            particulars: (existing.particulars as string)
              ?? (fixedRowsSpec[i]?.particulars as string)
              ?? '',
            value_cy: live.actualCy,
            value_py: live.actualPy,
            value_cy__rounded: live.roundedCy,
            value_py__rounded: live.roundedPy,
          });
          mutated = true;
        } else {
          overwritten.push(existing);
        }
      }
      if (!mutated) continue;
      // Pass 2: cascade the refreshed value-source values into the
      // computed rows that reference them. Without this, EPS profit
      // updates but basic_eps / diluted_eps stay at their pre-refresh
      // values until the auditor edits another cell — confusing.
      const recomputed = recomputeFixedGridFromSpec(
        overwritten,
        colSpec,
        fixedRowsSpec,
        (roundingFactorKey as RoundingFactor | null | undefined) ?? null,
        undefined,        // tolerance — default
        true,             // forceFreshComputed: discard prior tolerance-preserved rounded on computed rows
      );
      setDisclosure(spec.code, recomputed);
    }
  };

  if (specs.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <IconSystem.info size={20} style={{ marginBottom: 8, opacity: 0.5 }} />
        <div>No additional disclosures defined for this note.</div>
      </div>
    );
  }

  return (
    <div style={paneStyle}>
      {!readOnly && (
        <div style={paneToolbarStyle}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => autoHideEmptyDisclosures(specCodes)}
            title="Mark every disclosure section with no saved value as hidden from the printed PDF. Idempotent — re-running won't change anything once flagged. Reverse per-section via the eye toggle on each section header."
          >
            <IconSystem.eyeOff size={13} /> Hide empty
          </button>
          {Object.keys(liveByDisclosure).length > 0 && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleUpdateFromSources}
              disabled={!anyValueSourceDrift}
              title={
                anyValueSourceDrift
                  ? 'Refresh every value-sourced disclosure row (e.g. Note 35 EPS profit ← P&L XV) from the latest saved Statement value. Saved auditor inputs on the other rows (par value, shares, dilutive count) stay untouched. The Save button activates afterwards so the refreshed snapshot can be persisted.'
                  : 'No drift between saved value-source rows and their source Statements — nothing to update.'
              }
            >
              <IconSystem.refresh size={13} /> Update from Sources
            </button>
          )}
        </div>
      )}
      {specs.map(spec => {
        const isOpen = expanded.has(spec.code);
        const kind = detectKind(spec);
        const value = workspace.additional_disclosures[spec.code];
        const hidden = hiddenSet.has(spec.code);
        // Show the rounding-factor hint when the disclosure has any
        // amount column — signals the units the auditor should enter in.
        const hasAmountColumn = Array.isArray(spec.column_spec)
          && spec.column_spec.some(c => c.data_type === 'amount');
        const factorLabel = roundingFactorKey
          ? FACTOR_LABEL[roundingFactorKey] ?? roundingFactorKey
          : null;
        return (
          <div
            key={spec.code}
            style={hidden ? { ...sectionStyle, ...hiddenSectionStyle } : sectionStyle}
          >
            <div style={sectionHeaderRowStyle}>
              <button
                type="button"
                onClick={() => toggle(spec.code)}
                style={sectionHeaderButtonStyle}
              >
                {isOpen ? (
                  <IconSystem.chevronDown size={14} />
                ) : (
                  <IconSystem.chevronRight size={14} />
                )}
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={sectionLabelStyle}>
                    {spec.label}
                    {hidden && (
                      <span
                        style={hiddenBadgeStyle}
                        title="This section is hidden from the printed PDF. Use the eye toggle on the right to restore."
                      >
                        <IconSystem.eyeOff size={10} /> hidden in print
                      </span>
                    )}
                    {hasAmountColumn && factorLabel && (
                      <span style={factorHintStyle}>(₹ in {factorLabel})</span>
                    )}
                  </div>
                  {spec.description && (
                    <div style={sectionDescStyle}>{spec.description}</div>
                  )}
                </div>
                <span style={kindBadgeStyle(kind)}>{kind}</span>
              </button>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => toggleDisclosureHidden(spec.code)}
                  style={hiddenToggleStyle}
                  title={hidden ? 'Show this section in the printed PDF' : 'Hide this section from the printed PDF'}
                  aria-pressed={hidden}
                >
                  {hidden
                    ? <IconSystem.eyeOff size={14} />
                    : <IconSystem.eye size={14} />}
                </button>
              )}
            </div>
            {isOpen && (
              <div style={sectionBodyStyle}>
                <DisclosureRenderer
                  spec={spec}
                  kind={kind}
                  value={value}
                  onChange={v => setDisclosure(spec.code, v)}
                  readOnly={readOnly}
                  roundingFactorKey={roundingFactorKey}
                  displayMode={displayMode}
                  liveValuesByRowKey={liveByDisclosure[spec.code]}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ───── kind dispatcher ──────────────────────────────────────────────────────

type DisclosureKind = 'free_text' | 'computed' | 'fixed_grid' | 'variable_grid';

function detectKind(spec: DisclosureSpec): DisclosureKind {
  if (spec.free_text === true) return 'free_text';
  if (spec.is_computed_disclosure === true) return 'computed';
  if (Array.isArray(spec.fixed_rows) && spec.fixed_rows.length > 0) return 'fixed_grid';
  if (Array.isArray(spec.column_spec) && spec.column_spec.length > 0) return 'variable_grid';
  return 'free_text';
}

const DisclosureRenderer: React.FC<{
  spec: DisclosureSpec;
  kind: DisclosureKind;
  value: unknown;
  onChange: (v: unknown) => void;
  readOnly: boolean;
  roundingFactorKey?: string | null;
  displayMode: DisplayMode;
  /**
   * Per-disclosure subset of valueSourceLiveValues — keyed by row_key only
   * (the disclosure_code prefix is implicit). DisclosureFixedGrid uses it
   * for per-row drift detection vs the saved value_cy / value_py.
   */
  liveValuesByRowKey?: Record<string, DisclosureValueSourceLive>;
}> = ({ spec, kind, value, onChange, readOnly, roundingFactorKey, displayMode, liveValuesByRowKey }) => {
  if (kind === 'free_text') {
    return (
      <textarea
        className="form-input form-textarea"
        rows={4}
        value={typeof value === 'string' ? value : ''}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={e => onChange(e.target.value)}
        placeholder={`Enter ${spec.label.toLowerCase()}…`}
        style={{ width: '100%' }}
      />
    );
  }

  if (kind === 'computed') {
    return (
      <div style={computedPaneStyle}>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          Computed from:{' '}
          <code style={{ fontSize: '0.78rem' }}>{spec.formula ?? '—'}</code>
        </div>
        <div style={{ marginTop: 6, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {typeof value === 'number'
            ? value.toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '—'}
        </div>
      </div>
    );
  }

  // Variable grid — column_spec only, user adds rows.
  if (kind === 'variable_grid') {
    const cols = (spec.column_spec ?? []) as ColumnSpec[];
    const rows = Array.isArray(value) ? (value as Record<string, string | number | null>[]) : [];
    return (
      <DisclosureVariableGrid
        columns={cols}
        rows={rows}
        onChange={onChange}
        readOnly={readOnly}
        roundingFactor={roundingFactorKey}
        displayMode={displayMode}
      />
    );
  }

  // Fixed grid — column_spec + fixed_rows defining the row skeleton.
  if (kind === 'fixed_grid') {
    const cols = (spec.column_spec ?? []) as ColumnSpec[];
    const fixedRowsSpec = (spec.fixed_rows ?? []) as Parameters<typeof DisclosureFixedGrid>[0]['fixedRows'];
    const rows = Array.isArray(value) ? (value as Record<string, string | number | null>[]) : [];
    return (
      <DisclosureFixedGrid
        columns={cols}
        fixedRows={fixedRowsSpec}
        rows={rows}
        onChange={onChange}
        readOnly={readOnly}
        validations={spec.validations}
        roundingFactor={roundingFactorKey}
        displayMode={displayMode}
        liveValuesByRowKey={liveValuesByRowKey}
      />
    );
  }

  // Fallback — unknown kind.
  return <div style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>Unknown disclosure kind.</div>;
};

// ───── styles ────────────────────────────────────────────────────────────────

const paneStyle: React.CSSProperties = {
  padding: '0.75rem 0.75rem',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  overflow: 'hidden',
  background: 'var(--bg-primary)',
};
const paneToolbarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 6,
  paddingBottom: 2,
};

// Section header row hosts the expand/collapse trigger button AND the
// separate eye toggle for hide-in-print. Renders both side-by-side so
// the auditor can hide a section without expanding it first.
const sectionHeaderRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  background: 'var(--bg-secondary)',
};
const sectionHeaderButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  padding: '8px 12px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'var(--text-primary)',
};
const hiddenToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 12px',
  background: 'transparent',
  border: 'none',
  borderLeft: '1px solid var(--border-color)',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
};

// Hidden-in-print sections render visually muted so the auditor still
// sees the data on review but knows it won't appear in the deliverable
// PDF. Same hatched pattern as the Main Note hidden class row.
const hiddenSectionStyle: React.CSSProperties = {
  opacity: 0.6,
  background: 'repeating-linear-gradient(45deg, transparent, transparent 6px, var(--bg-secondary) 6px, var(--bg-secondary) 12px)',
};
const hiddenBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 6px',
  marginLeft: 6,
  borderRadius: 999,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};
const sectionLabelStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
};
const factorHintStyle: React.CSSProperties = {
  marginLeft: 8,
  padding: '1px 6px',
  borderRadius: 4,
  background: 'var(--bg-tertiary, #e2e8f0)',
  color: 'var(--text-secondary)',
  fontSize: '0.7rem',
  fontWeight: 500,
  letterSpacing: 0.2,
};
const sectionDescStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  marginTop: 2,
};
const sectionBodyStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderTop: '1px solid var(--border-color)',
};
const kindBadgeStyle = (kind: DisclosureKind): React.CSSProperties => ({
  fontSize: '0.65rem',
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: 4,
  background:
    kind === 'free_text' ? '#dbeafe' :
    kind === 'computed' ? '#fef3c7' :
    kind === 'fixed_grid' ? '#dcfce7' :
    '#e0e7ff',
  color:
    kind === 'free_text' ? '#1e40af' :
    kind === 'computed' ? '#92400e' :
    kind === 'fixed_grid' ? '#166534' :
    '#3730a3',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
});

const emptyStateStyle: React.CSSProperties = {
  padding: '32px',
  textAlign: 'center',
  color: 'var(--text-secondary)',
  fontSize: '0.85rem',
};

const computedPaneStyle: React.CSSProperties = {
  padding: 10,
  background: 'var(--bg-secondary)',
  borderRadius: 6,
};


export default DisclosuresPane;
